import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";

const Edit = ({ setVisible, fetchData, rowData }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [inputs, setInputs] = useState({
    title: "",
    start_date: "",
    end_date: "",
    description: "",
    status: "",
    reference_links: [],
    server_credentials: [],
    important_documents: [],
  });

  const [visibleFields, setVisibleFields] = useState([]); // Track which fields are visible

  // If used as a standalone component, fetch the project data
  useEffect(() => {
    const isStandalone = !rowData && params?.id;

    if (isStandalone) {
      const fetchProjectData = async () => {
        setFetchLoading(true);
        try {
          const res = await Http.get(`/project/edit/${params.id}`);
          if (res.data && res.data.data) {
            setProject(res.data.data);
            initializeForm(res.data.data);
          }
        } catch (error) {
          console.error("Error fetching project details:", error);
        } finally {
          setFetchLoading(false);
        }
      };

      fetchProjectData();
    } else if (rowData) {
      // If rowData is provided (used as a dialog), initialize from rowData
      initializeForm(rowData);
    }
  }, [rowData, params?.id]);

  const initializeForm = (data) => {
    // Initialize form with project data
    setInputs({
      title: data.title || "",
      start_date: data.start_date || "",
      end_date: data.end_date || "",
      description: data.description || "",
      status: data.status || "active",
      reference_links: data.reference_links || [],
      server_credentials: data.server_credentials || [],
      important_documents: data.documents || [],
    });

    // Set visible fields based on what data exists
    const fieldsToShow = [];
    if (data.reference_links && data.reference_links.length > 0) {
      fieldsToShow.push("reference_links");
    }
    if (data.server_credentials && data.server_credentials.length > 0) {
      fieldsToShow.push("server_credentials");
    }
    if (data.documents && data.documents.length > 0) {
      fieldsToShow.push("important_documents");
    }
    setVisibleFields(fieldsToShow);
  };

  const handleQuillChange = (value) => {
    setInputs((prevState) => ({ ...prevState, description: value }));
  };

  const handleReferenceLinksChange = (e, index, property) => {
    const value = e.target.value;
    setInputs((prevState) => {
      const updatedLinks = [...prevState.reference_links];
      // If the current item doesn't exist or isn't an object, initialize it
      if (!updatedLinks[index] || typeof updatedLinks[index] !== "object") {
        updatedLinks[index] = { title: "", url: "" };
      }
      // Update the specific property (title or url)
      updatedLinks[index] = { ...updatedLinks[index], [property]: value };
      return { ...prevState, reference_links: updatedLinks };
    });
  };

  const handleServerCredentialsChange = (e, index, property) => {
    const value = e.target.value;
    setInputs((prevState) => {
      const updatedCreds = [...prevState.server_credentials];
      // If the current item doesn't exist or isn't an object, initialize it
      if (!updatedCreds[index] || typeof updatedCreds[index] !== "object") {
        updatedCreds[index] = { server_name: "", url: "", username: "", password: "", additional_info: "" };
      }
      // Update the specific property
      updatedCreds[index] = { ...updatedCreds[index], [property]: value };
      return { ...prevState, server_credentials: updatedCreds };
    });
  };

  const handleChange = (e, index, field) => {
    const value = e.target.value;
    setInputs((prevState) => {
      const updatedField = [...prevState[field]];
      updatedField[index] = value;
      return { ...prevState, [field]: updatedField };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAddField = (field) => {
    if (!visibleFields.includes(field)) {
      setVisibleFields([...visibleFields, field]);

      // Initialize with one empty entry when the field is first added
      if (field === "reference_links") {
        setInputs((prevState) => ({
          ...prevState,
          reference_links: [...prevState.reference_links, { title: "", url: "" }],
        }));
      } else if (field === "server_credentials") {
        setInputs((prevState) => ({
          ...prevState,
          server_credentials: [...prevState.server_credentials, { server_name: "", url: "", username: "", password: "", additional_info: "" }],
        }));
      } else if (field === "important_documents") {
        setInputs((prevState) => ({
          ...prevState,
          important_documents: [...prevState.important_documents, ""],
        }));
      }
    }
  };

  const handleAddMore = (field) => {
    if (field === "reference_links") {
      setInputs((prevState) => ({
        ...prevState,
        reference_links: [...prevState.reference_links, { title: "", url: "" }],
      }));
    } else if (field === "server_credentials") {
      setInputs((prevState) => ({
        ...prevState,
        server_credentials: [...prevState.server_credentials, { server_name: "", url: "", username: "", password: "", additional_info: "" }],
      }));
    } else {
      setInputs((prevState) => ({
        ...prevState,
        [field]: [...prevState[field], ""],
      }));
    }
  };

  const handleRemoveField = (field, index) => {
    setInputs((prevState) => {
      const updatedField = [...prevState[field]];
      updatedField.splice(index, 1);
      return { ...prevState, [field]: updatedField };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const projectId = params?.id || project?.id || rowData?.id;

    try {
      const res = await Http.post(`/project/update/${projectId}`, inputs);
      if (res.data && res.data.status) {
        // If we're in dialog mode, update parent component
        if (setVisible && fetchData) {
          setVisible(false);
          fetchData();
        } else {
          // If we're in standalone mode, redirect to list page
          navigate(`/list-project`);
        }
      }
    } catch (error) {
      console.error("Error updating project:", error);
    } finally {
      setLoading(false);
    }
  };

  // Content rendering
  const renderContent = () => {
    // If we're loading data in standalone mode
    if (fetchLoading) {
      return (
        <div className="text-center p-5">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
          <p className="mt-2">Loading project details...</p>
        </div>
      );
    }

    // If we don't have data yet in standalone mode
    const isStandalone = !rowData && params?.id;
    if (isStandalone && !project) {
      return (
        <div className="text-center p-5">
          <i className="pi pi-exclamation-triangle" style={{ fontSize: "3rem", color: "#f59e0b" }}></i>
          <p className="mt-2">Project not found or has been deleted.</p>
          <Link to="/list-project" className="btn btn-primary mt-3">
            Back to Projects
          </Link>
        </div>
      );
    }

    // For the form rendering
    return (
      <div className={isStandalone ? "am-pagebody" : ""}>
        {loading ? (
          <div className="text-center p-5">
            <ProgressSpinner />
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              {/* Basic Fields */}
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Title */}
                  <div className="col-lg">
                    <label>
                      Title <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      placeholder="Enter Project Title"
                      value={inputs.title}
                      required
                      onChange={handleInputChange}
                    />
                  </div>
                  {/* Start Date */}
                  <div className="col-lg">
                    <label>Start Date</label>
                    <input type="date" className="form-control" name="start_date" value={inputs.start_date} required onChange={handleInputChange} />
                  </div>
                  {/* End Date */}
                  <div className="col-lg">
                    <label>End Date</label>
                    <input type="date" className="form-control" name="end_date" value={inputs.end_date} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="row mt-3">
                  {/* Status */}
                  <div className="col-lg">
                    <label>Status</label>
                    <select className="form-control" name="status" value={inputs.status} onChange={handleInputChange}>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="on-hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="row mt-3">
                  {/* Description */}
                  <div className="col-lg">
                    <label>
                      Description <i className="fa fa-star required"></i>
                    </label>
                    {/* React Quill Component */}
                    <ReactQuill
                      theme="snow"
                      value={inputs.description}
                      onChange={handleQuillChange}
                      placeholder="Enter Project Description"
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          ["bold", "italic", "underline"],
                          [{ color: [] }, { background: [] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link"],
                          ["clean"],
                        ],
                      }}
                      formats={["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"]}
                      style={{ height: "150px", marginBottom: "40px" }}
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  {/* File Upload */}
                  <div className="col-lg">
                    <label>File Upload</label>
                    <input type="file" className="form-control" name="documents" multiple />
                    <small className="text-muted">Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG</small>
                  </div>
                </div>

                {/* Dropdown for additional fields */}
                <div className="row mt-3">
                  {/* Dropdown */}
                  <div className="col-lg">
                    <div className="dropdown">
                      <button
                        className="btn btn-secondary dropdown-toggle"
                        type="button"
                        id="dropdownMenuButton"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        Add Fields
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        {/* Reference Links */}
                        <li>
                          <button className="dropdown-item" type="button" onClick={() => handleAddField("reference_links")}>
                            Reference Links
                          </button>
                        </li>
                        {/* Server Credentials */}
                        <li>
                          <button className="dropdown-item" type="button" onClick={() => handleAddField("server_credentials")}>
                            Server Credentials
                          </button>
                        </li>
                        {/* Important Documents */}
                        <li>
                          <button className="dropdown-item" type="button" onClick={() => handleAddField("important_documents")}>
                            Important Documents
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Reference Links */}
                {visibleFields.includes("reference_links") && (
                  <div className="row mt-4">
                    <div className="col-lg-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6>Reference Links</h6>
                        <button className="btn btn-secondary btn-sm ms-1" type="button" onClick={() => handleAddMore("reference_links")}>
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                      {inputs.reference_links.map((link, index) => (
                        <div className="d-flex align-items-center my-2" key={`reference_${index}`}>
                          {/* Title */}
                          <input
                            type="text"
                            className="form-control me-2"
                            value={link.title || ""}
                            placeholder={`Title ${index + 1}`}
                            style={{ width: "50%" }}
                            onChange={(e) => handleReferenceLinksChange(e, index, "title")}
                          />
                          {/* Reference Link */}
                          <input
                            type="text"
                            className="form-control me-2"
                            value={link.url || ""}
                            placeholder={`URL ${index + 1}`}
                            onChange={(e) => handleReferenceLinksChange(e, index, "url")}
                          />
                          <button className="btn btn-danger btn-sm" type="button" onClick={() => handleRemoveField("reference_links", index)}>
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Server Credentials */}
                {visibleFields.includes("server_credentials") && (
                  <div className="row mt-4">
                    <div className="col-lg-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6>Server Credentials</h6>
                        <button className="btn btn-secondary btn-sm ms-2" type="button" onClick={() => handleAddMore("server_credentials")}>
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                      {inputs.server_credentials.map((cred, index) => (
                        <div className="my-2" key={`server_${index}`}>
                          {/* URL Field */}
                          <div className="d-flex align-items-center mb-2">
                            {/* Server Name */}
                            <input
                              type="text"
                              className="form-control me-2"
                              value={cred.server_name || ""}
                              placeholder={`Server Name ${index + 1}`}
                              onChange={(e) => handleServerCredentialsChange(e, index, "server_name")}
                            />
                            {/* URL */}
                            <input
                              type="text"
                              className="form-control me-2"
                              value={cred.url || ""}
                              placeholder={`URL ${index + 1}`}
                              onChange={(e) => handleServerCredentialsChange(e, index, "url")}
                            />
                            <button className="btn btn-danger btn-sm" type="button" onClick={() => handleRemoveField("server_credentials", index)}>
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>

                          {/* Username and Password Fields */}
                          <div className="d-flex">
                            <input
                              type="text"
                              className="form-control me-2"
                              value={cred.username || ""}
                              placeholder={`Username ${index + 1}`}
                              onChange={(e) => handleServerCredentialsChange(e, index, "username")}
                            />
                            <input
                              type="password"
                              className="form-control"
                              value={cred.password || ""}
                              placeholder={`Password ${index + 1}`}
                              onChange={(e) => handleServerCredentialsChange(e, index, "password")}
                            />
                          </div>

                          {/* Additional Info */}
                          <div className="d-flex mt-2">
                            <input
                              type="text"
                              className="form-control"
                              value={cred.additional_info || ""}
                              placeholder={`Additional Info ${index + 1}`}
                              onChange={(e) => handleServerCredentialsChange(e, index, "additional_info")}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Important Documents */}
                {visibleFields.includes("important_documents") && (
                  <div className="row mt-4">
                    <div className="col-lg-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6>Important Documents</h6>
                        <button className="btn btn-secondary btn-sm ms-2" type="button" onClick={() => handleAddMore("important_documents")}>
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                      {inputs.important_documents.map((doc, index) => (
                        <div className="my-2" key={`document_${index}`}>
                          <div className="d-flex align-items-center">
                            <input
                              type="file"
                              className="form-control me-2"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                setInputs((prevState) => {
                                  const updatedDocuments = [...prevState.important_documents];
                                  updatedDocuments[index] = file;
                                  return {
                                    ...prevState,
                                    important_documents: updatedDocuments,
                                  };
                                });
                              }}
                            />
                            <button className="btn btn-danger btn-sm" type="button" onClick={() => handleRemoveField("important_documents", index)}>
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                          {doc && doc.name && (
                            <small className="text-muted">
                              Selected file: {doc.name} ({(doc.size / 1024).toFixed(2)} KB)
                            </small>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="row mt-4">
                  <div className="col-lg">
                    {isStandalone && (
                      <Link to="/list-project" className="btn btn-secondary">
                        <i className="pi pi-arrow-left me-1"></i> Back to Projects
                      </Link>
                    )}
                    <button className="btn btn-primary float-end" type="submit">
                      Update Project
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* IsLogin component is always shown in standalone mode */}
      {!rowData && params?.id && <IsLogin />}

      {/* Main layout for standalone mode */}
      {!rowData && params?.id && (
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Edit Project</h5>
          </div>
          {renderContent()}
        </div>
      )}

      {/* Simple layout for dialog mode */}
      {rowData && renderContent()}
    </div>
  );
};

export default Edit;
